import { defaultQueryBuilderRules } from 'search/AdvancedSearch/consts';
import {
  getFilterConditionByType,
  isMessageContent,
  isSurveyContent,
} from 'broadcasts/utils';
import {
  AdvancedSearchFilterCondition,
  APIFieldAttributes,
  APIModelAttributes,
  APISurveyAttributes,
  APIWorkspaceAttributes,
  BroadcastContent,
  BroadcastFilterCondition,
  BroadcastFilterConditionType,
  QueryBuilderSubquery,
} from '@ardoq/api-types';
import { AdvancedSearchConditionStreamShape } from './types';
import { ARDOQ_DEFAULT_LOCALE } from '@ardoq/locale';
import { modelOperations } from '@ardoq/core';
import { type FieldsState, toFieldTypeKey } from 'streams/fields/fields$';
import type {
  CollectionStream,
  StreamCollection,
} from 'streams/utils/streamUtils';

export const newAdvancedSearchCondition = (
  advancedQuery: QueryBuilderSubquery = defaultQueryBuilderRules
): AdvancedSearchFilterCondition => ({
  conditionType: BroadcastFilterConditionType.ADVANCED_SEARCH,
  advancedQuery,
});

export const getDefaultState = (): AdvancedSearchConditionStreamShape => ({
  isEnabled: false,
  isChecked: false,
  isExpanded: false,
  advancedSearchCondition: newAdvancedSearchCondition(),
  fields: [],
  locale: ARDOQ_DEFAULT_LOCALE,
});

export const findAdvancedSearchCondition = (
  filterConditions: BroadcastFilterCondition[]
) => {
  return getFilterConditionByType(
    filterConditions,
    BroadcastFilterConditionType.ADVANCED_SEARCH
  );
};

type ResolveContentTypeAndModelId = {
  content: BroadcastContent;
  workspaces: StreamCollection<APIWorkspaceAttributes>;
  surveys: CollectionStream<APISurveyAttributes>;
  models: StreamCollection<APIModelAttributes>;
};

const resolveContentTypeAndModelId = ({
  content,
  workspaces,
  surveys,
  models,
}: ResolveContentTypeAndModelId): {
  componentTypeId: string | null;
  modelId: string | null;
} => {
  if (isMessageContent(content)) {
    const workspace = content.workspaceId
      ? workspaces.byId[content.workspaceId]
      : undefined;
    return {
      componentTypeId: content.componentType ?? null,
      modelId: workspace?.componentModel ?? null,
    };
  }
  if (isSurveyContent(content)) {
    const survey = content.contentId
      ? surveys.byId[content.contentId]
      : undefined;
    const workspace = survey?.workspace
      ? workspaces.byId[survey.workspace]
      : undefined;
    const model = workspace ? models.byId[workspace.componentModel] : undefined;
    if (!model || !survey?.componentTypeName) {
      return { componentTypeId: null, modelId: null };
    }
    const componentType = Object.values(
      modelOperations.getComponentTypes(model).componentTypesById
    ).find(type => type.name === survey.componentTypeName);
    return {
      componentTypeId: componentType?.id ?? null,
      modelId: model._id,
    };
  }
  return { componentTypeId: null, modelId: null };
};

type GetFieldsForContentArgs = {
  content: BroadcastContent;
  workspaces: StreamCollection<APIWorkspaceAttributes>;
  surveys: CollectionStream<APISurveyAttributes>;
  models: StreamCollection<APIModelAttributes>;
  fieldsState: FieldsState;
};

/**
 * Resolve which fields are applicable to the broadcast content's selected
 * workspace + component type. Falls back to the full field list when the
 * content type isn't determined yet (no workspace/type picked, or content
 * is of a type that doesn't filter on component fields).
 */
export const getFieldsForContent = ({
  content,
  workspaces,
  surveys,
  models,
  fieldsState,
}: GetFieldsForContentArgs): APIFieldAttributes[] => {
  const { componentTypeId, modelId } = resolveContentTypeAndModelId({
    content,
    workspaces,
    surveys,
    models,
  });
  if (!componentTypeId || !modelId) return fieldsState.models;
  return (
    fieldsState.byComponentType.get(toFieldTypeKey(modelId, componentTypeId)) ??
    []
  );
};
